import React, { useState, useContext } from "react";
import { Paper, InputBase, IconButton, Tooltip } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { useSearchParams } from "react-router-dom";
import { useAuth } from "hooks";
import { MapContext } from "contexts/StateContext";
import {
  useLazyGetAddressByNameQuery,
  useLazyGetAddressOptionsByKeywordQuery,
} from "store/services/mapAddressApi";
import { useLazyGetPublicAddressByNameQuery } from "store/services/publicApi";
import { validateInput } from "utils/validateInput";
import { codeIntoCoord, coordsIntoCode } from "utils/coordinatesConvert";
import { truncateString } from "./utils/truncateString";

const SearchControl = ({ isExpanded = false, onAddressSelect }) => {
  const [searchValue, setSearchValue] = useState("");
  const { loggedInUser } = useAuth();

  const {
    setSearchKeywords,
    setCoords,
    setCode,
    setSearchCoords,
    setAddressData,
    setShowRegisterForm,
    setAddressFormHydratingValues,
    setIsSearching,
  } = useContext(MapContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const _paramName = searchParams.get("name") || null;
  const _paramCode = searchParams.get("code") || null;
  const isUpdateAddress = searchParams.get("update") === "true";

  const [searchError, setSearchError] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);

  const fetchAddressQuery = loggedInUser
    ? useLazyGetAddressByNameQuery
    : useLazyGetPublicAddressByNameQuery;

  const [
    fetchAddressByName,
    { data: addressDataByName },
  ] = fetchAddressQuery();

  const [fetchOptions, { isFetching: fetchingOptions }] =
    useLazyGetAddressOptionsByKeywordQuery();

  React.useEffect(() => {
    if (!_paramName || !isUpdateAddress) return;
    fetchAddressByName(_paramName);
    setSearchValue(_paramName);
  }, [_paramName, fetchAddressByName, isUpdateAddress]);

  React.useEffect(() => {
    if (!_paramCode) return;
    setSearchValue(_paramCode);
  }, [_paramCode]);

  React.useEffect(() => {
    if (!isUpdateAddress) return;

    if (addressDataByName) {
      setShowRegisterForm(true);
      setAddressFormHydratingValues(addressDataByName);
      setSearchCoords(addressDataByName.coordinates);
    }
  }, [
    addressDataByName,
    isUpdateAddress,
    setAddressFormHydratingValues,
    setSearchCoords,
    setShowRegisterForm,
  ]);

  const handleSearch = async () => {
    try {
      setSearchError(null);
      setAddressData(null);
      setSearchCoords(null);
      setShowRegisterForm(false);
      searchParams.delete("update");
      setIsSearching(true);

      const optionsResponse = await fetchOptions(searchValue);
      if (optionsResponse?.data) {
        const options = optionsResponse.data;
        setSearchOptions(options?.length > 10 ? options.slice(0, 10) : options);
      }

      if (validateInput(searchValue).isCode) {
        setSearchCoords(codeIntoCoord(searchValue));
        setSearchParams({ code: searchValue });
        return;
      }

      if (validateInput(searchValue).isAlphaNumeric) {
        setSearchParams({ name: searchValue });
        const addressResponse = await fetchAddressByName(searchValue);
        if (addressResponse?.data) {
          setAddressData(addressResponse.data);
          setSearchCoords(addressResponse.data.coordinates);
        } else if (addressResponse?.error) {
          setSearchError("Couldn't find saved address with input name!");
          setAddressData(null);
        }
        return;
      }

      if (validateInput(searchValue).isInvalid) {
        setSearchError("Invalid input format!");
        return;
      }
    } catch (error) {
      console.error("Error during search:", error);
      setSearchError("An unexpected error occurred during the search.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleReset = () => {
    setSearchValue("");
    setSearchOptions([]);
    setAddressData(null);
    setSearchCoords(null);
    setSearchKeywords(null);
    setSearchError(null);
    setShowRegisterForm(false);
  };

  const handleOptionClick = (option) => {
    const coords = {
      lat: Number(option.lat),
      lng: Number(option.lon),
    };

    setSearchCoords(coords);
    setCoords({
      lat: coords.lat.toFixed(6),
      lng: coords.lng.toFixed(6),
    });
    setCode(coordsIntoCode(coords));

    if (onAddressSelect) {
      onAddressSelect(option);
    }
  };

  return (
    <div
      className={`w-full transition-all duration-300 ${
        isExpanded ? "h-full bg-white p-4" : "px-4"
      }`}
    >
      <Paper
        elevation={3}
        className="flex w-full flex-col items-center px-4 py-2"
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          borderRadius: "12px",
          width: "100%",
        }}
      >
        <div className="flex w-full items-center gap-2">
          <LocationOnIcon className="text-gray-400" />
          <InputBase
            className="text-sm"
            placeholder="Search CAN Address"
            value={searchValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchValue) {
                handleSearch(searchValue);
              }
            }}
            onChange={(e) => {
              const upperCaseValue = e.target.value.toUpperCase();
              setSearchValue(upperCaseValue);
              setSearchKeywords(upperCaseValue);
            }}
            sx={{
              width: "100%",
              color: "#000",
              "&::placeholder": {
                color: "#555",
              },
            }}
          />

          <Tooltip title="Search">
            <IconButton
              className="bg-transparent hover:bg-gray-100"
              size="small"
              onClick={() => handleSearch(searchValue)}
              sx={{
                color: "#000",
                "&:hover": {
                  color: "#000",
                },
              }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          {searchValue && (
            <Tooltip title="Reset">
              <IconButton
                className="bg-transparent hover:bg-gray-100"
                size="small"
                onClick={handleReset}
                sx={{
                  color: "#000",
                  "&:hover": {
                    color: "#000",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {searchError && (
          <div className="mt-2 pl-2 text-red-500">{searchError}</div>
        )}

        {!fetchingOptions && searchOptions?.length > 0 && (
          <div className="mt-4 w-full">
            <div className="mb-2 ml-2 text-sm font-semibold text-black">
              Related Places:
            </div>
            <div className="max-h-52 space-y-2 overflow-y-auto rounded-lg bg-purple-50 p-2">
              {searchOptions?.map((option) => (
                <div
                  className="flex cursor-pointer items-center justify-between rounded-md bg-purple-100 p-2 hover:bg-purple-200"
                  key={option.place_id}
                  onClick={() => handleOptionClick(option)}
                  style={{
                    color: "#000",
                    transition: "background-color 0.2s ease-in-out",
                  }}
                >
                  <LocationOnIcon style={{ color: "grey" }} />
                  <div className="text-sm text-black">
                    {truncateString(option.display_name, 55)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default SearchControl;
